import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { 
    opacity: 0; 
    transform: scale(0.9); 
  }
  to { 
    opacity: 1; 
    transform: scale(1); 
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 40px;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalContainer = styled.div`
  position: relative;
  max-width: 80vw;
  max-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${slideIn} 0.3s ease;
`;

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 75vh;
  min-height: 200px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: -60px;
  right: 0;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
  transition: all 0.2s ease;
  z-index: 10001;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: white;
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    top: -70px;
    right: 0;
    width: 48px;
    height: 48px;
  }
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
  transition: all 0.2s ease;
  z-index: 10001;
  
  &:hover {
    background: white;
    transform: translateY(-50%) scale(1.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: translateY(-50%);
  }
  
  ${props => props.direction === 'left' ? 'left: -70px;' : 'right: -70px;'}
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    ${props => props.direction === 'left' ? 'left: -60px;' : 'right: -60px;'}
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: -50px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    bottom: -60px;
    font-size: 13px;
    padding: 6px 12px;
  }
`;

const LoadingIndicator = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ImageModal = ({ 
  images = [], 
  currentIndex = 0, 
  isOpen = false, 
  onClose, 
  onNext, 
  onPrevious 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(currentIndex);
  const [isLoading, setIsLoading] = useState(true);

  // Update internal index when prop changes
  useEffect(() => {
    setCurrentImageIndex(currentIndex);
  }, [currentIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, currentImageIndex]);

  const handleNext = () => {
    if (currentImageIndex < images.length - 1) {
      const newIndex = currentImageIndex + 1;
      setCurrentImageIndex(newIndex);
      if (onNext) onNext(newIndex);
      setIsLoading(true);
    }
  };

  const handlePrevious = () => {
    if (currentImageIndex > 0) {
      const newIndex = currentImageIndex - 1;
      setCurrentImageIndex(newIndex);
      if (onPrevious) onPrevious(newIndex);
      setIsLoading(true);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
  };

  if (!isOpen || !images.length) return null;

  const currentImage = images[currentImageIndex];
  const hasMultipleImages = images.length > 1;

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <CloseButton onClick={onClose} aria-label="Close">
          <X size={20} />
        </CloseButton>

        {hasMultipleImages && (
          <NavButton
            direction="left"
            onClick={handlePrevious}
            disabled={currentImageIndex === 0}
            aria-label="Previous image"
          >
            <ChevronLeft size={24} />
          </NavButton>
        )}

        <ImageContainer>
          {isLoading && <LoadingIndicator />}
          <ModalImage
            src={currentImage.url || currentImage.s3_key || currentImage}
            alt={`Image ${currentImageIndex + 1} of ${images.length}`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: isLoading ? 'none' : 'block' }}
          />
        </ImageContainer>

        {hasMultipleImages && (
          <NavButton
            direction="right"
            onClick={handleNext}
            disabled={currentImageIndex === images.length - 1}
            aria-label="Next image"
          >
            <ChevronRight size={24} />
          </NavButton>
        )}

        {hasMultipleImages && (
          <ImageCounter>
            {currentImageIndex + 1} of {images.length}
          </ImageCounter>
        )}
      </ModalContainer>
    </Overlay>
  );
};

export default ImageModal;